import { lazy, Suspense, useEffect, useRef, type MutableRefObject } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AscentCanvas } from './three/AscentCanvas';
import { initScrollSystem } from './motion/scroll';
import { WhatsAppFloatingButton } from './components/site/WhatsAppFloatingButton';

/**
 * Route-level code splitting. Landing pulls in three/@react-three/fiber +
 * drei + gsap + lenis — that stack must not be paid for by someone visiting
 * /legal/terms or /blog. Every route is its own chunk so interior pages stay
 * light (spec Page 8.4 budget: total JS < 300KB gzipped for a given page).
 */
const Landing = lazy(() => import('./routes/Landing'));
const Events = lazy(() => import('./routes/Events'));
const EventDetail = lazy(() => import('./routes/EventDetail'));
const Blog = lazy(() => import('./routes/Blog'));
const BlogPost = lazy(() => import('./routes/BlogPost'));
const About = lazy(() => import('./routes/About'));
const Panel = lazy(() => import('./routes/Panel'));
const Sponsors = lazy(() => import('./routes/Sponsors'));
const Terms = lazy(() => import('./routes/legal/Terms'));
const Privacy = lazy(() => import('./routes/legal/Privacy'));
const Refund = lazy(() => import('./routes/legal/Refund'));
const Contact = lazy(() => import('./routes/legal/Contact'));
const Pricing = lazy(() => import('./routes/legal/Pricing'));
const NotFound = lazy(() => import('./routes/NotFound'));

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-void">
      <span className="font-mono text-xs uppercase tracking-widest text-faint">Loading&hellip;</span>
    </div>
  );
}

/**
 * "The Ascent" is mounted once here, above `<Routes>`, instead of per-page —
 * React Router keeps this sibling alive across navigations, so the 3D layer
 * (and its particle/converged state) persists site-wide instead of
 * remounting on every route change. Trades the old "Landing pays for
 * three.js, no one else does" bundle budget for a consistent animated
 * background everywhere, per an explicit user request — capability tiering
 * (AscentCanvas -> StaticFallback for low-end/no-WebGL/reduced-motion) still
 * protects those visitors from paying for it at all.
 */
function AscentLayer({ scrollRef }: { scrollRef: MutableRefObject<number> }) {
  const { pathname } = useLocation();
  const isLanding = pathname === '/';

  useEffect(() => {
    if (isLanding) return;
    // Non-landing pages have no scroll-jacked hero narrative — just settle
    // the rocket into its fully-assembled, idle-spinning resting state.
    scrollRef.current = 1;
  }, [isLanding, scrollRef]);

  return <AscentCanvas scrollRef={scrollRef} layoutMode={isLanding ? 'hero' : 'ambient'} />;
}

export default function App() {
  const scrollRef = useRef(0);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const system = initScrollSystem(scrollRef, { reducedMotion });
    return () => system.destroy();
  }, []);

  return (
    <>
      <AscentLayer scrollRef={scrollRef} />
      <WhatsAppFloatingButton />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:slug" element={<EventDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about" element={<About />} />
          <Route path="/panel" element={<Panel />} />
          <Route path="/sponsors" element={<Sponsors />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/refund" element={<Refund />} />
          <Route path="/legal/contact" element={<Contact />} />
          <Route path="/legal/pricing" element={<Pricing />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
